import type { FeatureFlag } from '@/state/useFeatureFlags';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { DataTable, useDataControl } from '@/components/DataTable';
import { Plus } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Panel } from '../../../Dashboard/ConsolePanel';

type EditorTabProps = {
  flags: FeatureFlag[];
  onCreateClick: () => void;
};

const CHIP = 'micro-label rounded-pill inline-flex w-fit border px-2 py-0.5';

/** Deployed is the shipped end state; anything else is still a draft. */
const statusChip = (status: string) =>
  status === 'deployed' ? 'border-accent-line text-accent' : 'border-sel-line text-sel';

export const EditorTab = ({ flags, onCreateClick }: EditorTabProps) => {
  const dataController = useDataControl<FeatureFlag>(flags);

  return (
    <Panel
      title="Editor"
      actions={
        <Button onClick={onCreateClick} className="h-9 rounded-lg">
          <Plus className="mr-1.5 h-4 w-4" /> Create Branch Flag
        </Button>
      }
      bodyClassName="p-3"
    >
      <DataTable<FeatureFlag>
        dataController={dataController}
        pageSize={10}
        noDataMessage="No branch flags for the editor yet."
        columns={[
          {
            field: 'name',
            label: 'Name',
            sortKey: 'name',
            headerClassName: 'micro-label',
            cellClassName: 'min-w-0',
            format: ({ item }) => (
              <Link to={`/admin/feature-flags/${item.id}`} className="text-foreground block truncate hover:underline">
                {item.name}
              </Link>
            ),
          },
          {
            field: 'status',
            label: 'Status',
            sortKey: 'status',
            headerClassName: 'micro-label w-32',
            format: ({ value }) => <span className={cn(CHIP, statusChip(value as string))}>{value as string}</span>,
          },
          {
            field: 'createdAt',
            label: 'Created At',
            sortKey: 'createdAt',
            headerClassName: 'micro-label w-32',
            cellClassName: 'num text-dim',
            format: ({ value }) => new Date(value as string).toLocaleDateString(),
          },
        ]}
      />
    </Panel>
  );
};
